<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Cargos" :on-add="() => router.push({ name: 'create-roles' })" />
    <data-table
      ref="datatable"
      :headers="headers"
      :provider="fetch"
      :filters="[]"
      :actions="actions"
      :vertical="xs"
    >
      <template #[`item.id`]="{ item }">
        {{ item.id.split('-')[0] }}
      </template>
    </data-table>
  </section>
</template>

<script setup lang="ts">
import TPageTitle from '@/components/TPageTitle.vue'
import type {
  DataTableHeader,
  DefaultDataTableActions,
} from '@/types/DataTable'
import DataTable from '@/components/datatable/DataTable.vue'
import { useAbility } from '@casl/vue'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import { useRouter } from 'vue-router'
import RoleService from '@/services/RoleService'
import Role from '@/types/Role'
import { useDialog } from '@/stores/dialog'
import { useTemplateRef } from 'vue'
import { useDisplay } from 'vuetify'

const { can } = useAbility()
const snackbar = useSnackBar()
const loader = useLoader()
const router = useRouter()
const dialog = useDialog()
const { xs } = useDisplay()

const datatable = useTemplateRef('datatable')

const headers: DataTableHeader[] = [
  { title: 'Código', value: 'id', key: 'id', sortable: false },
  { title: 'Nome', value: 'title', key: 'title', sortable: false },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
]

const actions: DefaultDataTableActions<Role> = {
  update: {
    callback: (item: Role) => router.push({ name: 'edit-roles', params: { id: item.id } }),
    condition: (item: Role) => can('update', 'roles') && item.name !== 'master',
    color: 'info',
  },
  destroy: {
    callback: destroy,
    condition: (item: Role) => can('delete', 'roles') && item.name !== 'master',
    color: 'error',
  },
}

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    loader.increment()
    return await (new RoleService()).index(page, filters, per_page)
  } finally {
    loader.decrement()
  }
}

async function destroy(item: Role) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: `Realmente deseja excluir o cargo ${item.title}? Esta ação é irreversível e os usuários perderão as permissões concedidas por ele.`,
  })

  if (!confirm) return

  try {
    loader.increment();

    (new RoleService()).destroy(item)

    snackbar.show(`Cargo ${item.title} excluido com sucesso.`, 'success')

    datatable.value?.search()
  } finally {
    loader.decrement()
  }
}

</script>
